import {useEffect, useMemo, useState} from 'react';
import {NavLink} from 'react-router';
import type {P02ReviewInboxViewModel, SmtRuntimePort} from '../contracts.ts';

export function QuickPendingSheet({
  open,
  onClose,
  runtime,
  view,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly runtime: SmtRuntimePort;
  readonly view: P02ReviewInboxViewModel;
}) {
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (!open) setSelectedId('');
  }, [open]);

  const selected = useMemo(
    () => view.reviews.find((review) => review.reviewId === selectedId),
    [selectedId, view.reviews],
  );
  const detail = selected ? view.detailsByReviewId[selected.reviewId] : undefined;

  if (!open) return null;

  return (
    <div className="smt-control-backdrop" role="presentation">
      <aside
        className={`smt-control-sheet quick-pending-sheet${detail ? ' show-detail' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="快捷待處理"
      >
        <header>
          <div>
            <small>營運待處理</small>
            <h2>{detail ? `${detail.sourceLabel} · ${detail.orderIdLabel}` : '待處理'}</h2>
          </div>
          <button type="button" aria-label="關閉待處理" onClick={onClose}>×</button>
        </header>

        {!detail ? (
          <>
            <div className="quick-pending-list">
              {view.reviews.length ? view.categories.map((category) => {
                const rows = view.reviews.filter((review) => review.categoryId === category.categoryId);
                if (!rows.length) return null;
                return (
                  <section key={category.categoryId}>
                    <h3>{category.label}<span>{category.countLabel}</span></h3>
                    {rows.map((review) => (
                      <button
                        type="button"
                        key={review.reviewId}
                        onClick={() => setSelectedId(review.reviewId)}
                      >
                        <div>
                          <b>{review.orderIdLabel} · {review.sourceLabel}</b>
                          <span>{review.issueLabel} · {review.timeLabel}</span>
                        </div>
                        <strong>{review.amountLabel}</strong>
                      </button>
                    ))}
                  </section>
                );
              }) : <p className="quick-pending-empty">目前冇待處理訂單。</p>}
            </div>
            <footer>
              <NavLink to="/reviews" onClick={onClose}>進入完整待處理</NavLink>
              <button type="button" onClick={onClose}>返回</button>
            </footer>
          </>
        ) : (
          <>
            <div className="quick-pending-detail">
              <div className="quick-pending-kpis">
                <div><span>來源</span><b>{detail.sourceLabel}</b></div>
                <div><span>狀態</span><b>{detail.statusLabel}</b></div>
                <div><span>識別碼</span><b>{detail.codeLabel}</b></div>
                <div><span>提交</span><b>{detail.submittedAtLabel}</b></div>
              </div>

              <section className="quick-pending-compare">
                <h3>核對資料</h3>
                {detail.comparisonRows.map((row) => (
                  <article key={row.rowId}>
                    <b>{row.label}</b>
                    <div>
                      <small>客人提交</small>
                      {row.submittedLines.map((line, index) => <span key={index}>{line}</span>)}
                    </div>
                    <div>
                      <small>系統資料</small>
                      {row.authorityLines.map((line, index) => <span key={index}>{line}</span>)}
                      {row.authorityTotalLabel ? <strong>{row.authorityTotalLabel}</strong> : null}
                      {row.displayDeltaLabel ? <em>{row.displayDeltaLabel}</em> : null}
                    </div>
                  </article>
                ))}
              </section>

              {detail.alerts.length ? (
                <section className="quick-pending-alerts">
                  {detail.alerts.map((alert) => (
                    <article key={alert.alertId}>
                      <div><b>{alert.title}</b><span>{alert.body}</span></div>
                      {alert.action ? (
                        <button
                          type="button"
                          onClick={() => runtime.dispatch({
                            type: 'p02.alert-action.requested',
                            reviewId: detail.reviewId,
                            alertId: alert.alertId,
                            actionId: alert.action!.actionId,
                          })}
                        >
                          {alert.action.label}
                        </button>
                      ) : null}
                    </article>
                  ))}
                </section>
              ) : null}
            </div>

            <footer>
              <button type="button" onClick={() => setSelectedId('')}>返回列表</button>
              <div className="quick-pending-actions">
                {detail.actions.map((action) => (
                  <button
                    type="button"
                    key={action.actionId}
                    className={action.kind}
                    disabled={!action.enabled}
                    onClick={() => runtime.dispatch({
                      type: 'p02.review-action.requested',
                      reviewId: detail.reviewId,
                      actionId: action.actionId,
                    })}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
}
